import React from "react";
import Avatar from "@mui/material/Avatar";
import * as R from "ramda";
import pathOr from "ramda/src/pathOr";
import path from "ramda/src/path";
import ifElse from "ramda/src/ifElse";
import compose from "ramda/src/compose";
import always from "ramda/src/always";
import concat from "ramda/src/concat";
import includes from "ramda/src/includes";
import pipe from "ramda/src/pipe";

import { useUserContext } from "../../contexts/UserContext";
import { Grid } from "../../UI/Grid";
import { Paper } from "../../UI/Paper";
import { Typography } from "../../UI/Typography";
import { Skeleton } from "../../UI/Skeleton";
import { UserDetail } from "../UserDetail";
import { pathOrNA } from "../../helpers/pathOrNA";

import { UserSocialAccounts } from "../UserSocialAccounts";
import { useNetwork } from "../../hooks/useNetwork";
import { pathOrFalse } from "../../helpers/pathOrFalse";
import { capitalize } from "../../helpers/capitalize";
import { IUser } from "../../constants/types";
import useAuth from "../../routes/login/AuthContext";
import {UserRemoveButton} from "../UserRemoveButton";

const AVATAR_SIZE = 56;

const providerPath = ["providerInfo", 0, "provider"];
const providerEmailPath = ["providerInfo", 0, "providerEmail"];

const getProviderId = compose<any, any, any>(
  ifElse(includes("@"), concat(" / "), always("")),
  pathOrNA(providerEmailPath)
);

//const TEST_TEXT ="Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis, ultricies nec, pellentesque eu, pretium quis, sem. Nulla consequat massa quis enim. Donec pede justo, fringilla vel, aliquet nec, vulputate eget, arcu. In enim justo, rhoncus ut, imperdiet a, venenatis vitae, justo. Nullam dictum felis eu pede mollis pretium. Integer tincidunt. Cras dapibus. Vivamus elementum semper nisi. Aenean vulputate eleifend tellus. Aenean leo ligula, porttitor eu, consequat vitae, eleifend ac, enim. Aliquam lorem ante, dapibus in, viverra quis, feugiat a, tellus. Phasellus viverra nulla ut metus varius laoreet. Quisque rutrum. Aenean imperdiet. Etiam ultricies nisi vel augue. Curabitur ullamcorper ultricies n"

const handleLoginMethod = (user: IUser) => `${path(providerPath, user)}${getProviderId(user)}`;

const getProvider = compose(
  capitalize,
  ifElse(
    pipe(
      path(providerPath) as (obj: any) => string,
      includes(R.__, ['google', 'facebook', "apple"])
    ),
    handleLoginMethod, 
    pathOr("User has not registered yet", providerPath)
  )
);

export const UserDetails = () => {
  const { user, loadUser, isLoading } = useUserContext();
  const { selecteNetworkAsync, selectedNetwork } = useNetwork();
  const {isSuperAdminWrite} = useAuth();

  React.useEffect(() => {
    if (!user?.l1Id) return;
    selecteNetworkAsync(user?.l1Id);
  }, [user?.l1Id, selecteNetworkAsync]);

  const isLoginMethodHighlighted =
    pathOrFalse(["providerInfo", 0, "provider"], user) && pathOrFalse(["providerInfo", 0, "providerId"], user);

  return (
    <Grid container spacing={3}>
      <Grid item container spacing={1} alignItems="center">
        <Grid item>
          {!isLoading && user && <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }} />}
          {isLoading && <Skeleton width={AVATAR_SIZE} height={AVATAR_SIZE} variant="circular" />}
        </Grid>
        <Grid item>
          {!isLoading && user && (
            <Typography fontSize={28} fontWeight={500}>
              {user?.firstName} {user?.lastName}
            </Typography>
          )}
          {isLoading && <Skeleton width={150} variant="text" />}
        </Grid>
      </Grid>
      <Paper sx={{ p: 3, mb: "20px", mt: "20px", ml: "20px", width: "100%"}}>
      <Grid item container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight={500}>
            User Details
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <UserDetail
                isLoading={isLoading}
                label="Login Method:"
                detail={getProvider(user as any)}
                isHighlighted={isLoginMethodHighlighted}
              />
            </Grid>
            <Grid item xs={12}>
              <UserDetail
                label="Preferred Email:"
                isLoading={isLoading}  
                detail={pathOrNA(["preferredEmail"], user)} 
              />
            </Grid>
            <Grid item xs={12}>
              <UserDetail
                label="Preferred First Name:"
                isLoading={isLoading}
                detail={pathOrNA(["preferredFirstName"], user)}
              />
            </Grid>
            <Grid item xs={12}>
              <UserDetail
                label="Preferred Last Name:"
                detail={pathOrNA(["preferredLastName"], user)}
                isLoading={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <UserDetail
                label="Personal Pronouns:"
                detail={pathOrNA(["personalPronoun"], user)}
                isLoading={isLoading}
              />
            </Grid>
            
            {selectedNetwork.userPhoneNumbersEnabled && (
              <Grid item xs={12}>
                <UserDetail isLoading={isLoading} label="Phone Number:" detail={`+${pathOrNA(["phoneCallingCode"], user)}${pathOrNA(["phoneNumber"], user)}`} />
              </Grid>
            )}
            
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <UserDetail label="User Id:" detail={pathOrNA(["userId"], user)} isLoading={isLoading} />
            </Grid>
          </Grid>

         
        </Grid>
        <Grid item xs={6}>
          <Grid item xs={12}>
            <UserSocialAccounts />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
            <UserDetail
              label="Bio"
              detail={pathOrNA(["bio"], user)}
              isLoading={isLoading}
            />
          </Grid>
        
      {user && isSuperAdminWrite() && (
            <Grid item xs={12} mt={3}>
              <UserRemoveButton name={user.firstName + " " + user.lastName} userId={user.userId} onSuccess={loadUser}/>
            </Grid>
        )}
      </Paper>
    </Grid>
  );
};
